import { useMemo } from "react";
import { IAuthorizer, useAuthorizer } from "./action-authorizer";
import actions from "./actions";
import { SECTION } from "./constants";

type Action = { id: string; type: "property" | "global"; enablingScopes: string[] };

function isViewAuthorized(authorizer: IAuthorizer, actions: Action[]) {
  return actions.some(action => authorizer.canExecute(action));
}

export function usePermissions() {
  const authorizer = useAuthorizer();

  return useMemo(() => {
    return [
      /** MAIN SECTIONS **/
      {
        section: SECTION.MAIN_RESEREVATIONS,
        show: isViewAuthorized(authorizer, [
          actions.ListReservations,
          actions.ListReservationsExtended,
        ]),
      },
      {
        section: SECTION.MAIN_INVENTORIES,
        show: isViewAuthorized(authorizer, [
          actions.ViewInventory,
          actions.ViewExtra,
          // TODO: add INVENTORY_CONFIGURATION
          actions.ViewPromo,
          actions.ViewDynamicPriceRule,
        ]),
      },
      {
        section: SECTION.MAIN_INTEGRATIONS,
        show: isViewAuthorized(authorizer, [
          actions.ViewGoogleAnalyticsSettings,
          actions.ViewCustomMarkup,
        ]),
      },
      {
        section: SECTION.MAIN_REPORTS,
        show: isViewAuthorized(authorizer, [
          actions.ViewReservationStats,
          actions.ViewStaysPerDay,
          actions.ListReservationsExtended,
          actions.ViewExtendedReservationStats,
          actions.ViewBillingReport,
          // TODO: add PRICE_FEED
        ]),
      },
      {
        section: SECTION.MAIN_COMMUNICATIONS,
        show: isViewAuthorized(authorizer, [
          actions.ViewWebMessage,
          actions.ViewAlertSettings,
          actions.ViewConfirmationEmailSettings,
          actions.ViewEmailListing,
          actions.ViewPreStayEmailSettings,
          actions.ViewPostStayEmailSettings,
          actions.ViewPostStayPollSettings,
          actions.ViewPostStayPollResults,
          actions.ViewForm,
          actions.ViewCustomEmail,
        ]),
      },
      {
        section: SECTION.MAIN_SETTINGS,
        show: isViewAuthorized(authorizer, [
          actions.ViewPropertyInfo,
          actions.ViewBehaviorSettings,
          actions.ViewPropertyParameters,
          actions.ViewAppeareanceSettings,
          actions.ViewDisplay,
          actions.ChangeOwnPassword,
          actions.ViewLegalInfo,
          actions.ViewReservationModificationPolicy,
          actions.ViewFilter,
          actions.ViewCancellationPolicy,
        ]),
      },
      {
        section: SECTION.MAIN_LOYALTY,
        show: isViewAuthorized(authorizer, [
          actions.ViewLoyaltyCustomers,
          actions.ViewDerivedLoyaltyTiers,
          actions.ViewConstantLoyaltyTiers,
          actions.ViewLoyaltyOrganizations,
        ]),
      },
      {
        section: SECTION.MAIN_USERS,
        show: isViewAuthorized(authorizer, [
          actions.ViewPropertyUsers,
          actions.CreateProperty,
          actions.CreateUser,
          actions.ModifyAccessToProperty,
          actions.ChangeUserPassword,
          // TODO: add CHANGE_USER_GROUP
          actions.ReplicateProperty,
          // TODO: add CONNECTIONS_WIT_CHANNEL
          // TODO: add USERS_JAVA_PROPERTY_TO_EXCEL
          // TODO: add GOOGLE_HOTEL_FINDER
          actions.ExecuteBatchSQLQuery,
          actions.CreateUserBatch,
          actions.EditUserBatch,
        ]),
      },
      {
        section: SECTION.MAIN_INTERNAL_INTEGRATIONS,
        show: isViewAuthorized(authorizer, [
          actions.ViewSiteminderSettings,
          actions.ViewTripAdvisorRESettings,
          actions.ViewRategainSettings,
          actions.ViewParityRateSettings,
          actions.ViewIntegrationParameters,
          actions.ViewPropertyConfiguration,
          actions.ViewChannelLogs,
          actions.ResendReservationToChannel,
          actions.ViewGoogleFeed,
          actions.ViewQueryExecutor,
          actions.UpdatePropertyChannelManagers,
          actions.ViewChainConfigurer,
          actions.ViewCalendarRegen,
        ]),
      },
      {
        section: SECTION.MAIN_OLD_CRM,
        show: authorizer.canExecute(actions.ViewContract),
      },
      {
        section: SECTION.MAIN_CRM,
        show: isViewAuthorized(authorizer, [
          actions.ViewClient,
          actions.ViewCrmProperty,
          actions.ViewService,
          actions.ViewContract,
          actions.ViewPartner,
          actions.GenerateBillingReport,
          actions.ViewBillingReport,
        ]),
      },
      {
        section: SECTION.MAIN_UTILITIES,
        show: isViewAuthorized(authorizer, [
          actions.ViewPropertyParameters,
          actions.CheckInventoryIntegrity,
          // TODO: add COMPARE_DATABASE
        ]),
      },
      {
        section: SECTION.MAIN_ADMIN_CONTROL_PANEL,
        show: isViewAuthorized(authorizer, [
          // TODO: add SYSTEM_CONTROL_PANEL
          // TODO: add BILLING_EVOLUTION
        ]),
      },
      /** END MAIN SECTIONS **/
      {
        section: SECTION.DASHBOARD,
        show: authorizer.canExecute(actions.ViewDashboard),
      },
      {
        section: SECTION.RESERVATIONS_LIST,
        show: authorizer.canExecute(actions.ListReservations),
        modify: authorizer.canExecute(actions.ModifyReservation),
        delete: authorizer.canExecute(actions.CancelReservation),
      },
      {
        section: SECTION.RESERVATIONS_LIST_EXTENDED,
        show: actions.ListReservationsExtended,
        modify: actions.ModifyReservation,
        delete: actions.CancelReservation,
      },
      {
        section: SECTION.INVENTORY_MANAGEMENT,
        show: authorizer.canExecute(actions.ViewInventory),
        modify: authorizer.canExecute(actions.ModifyInventory),
      },
      {
        section: SECTION.EXTRAS,
        show: authorizer.canExecute(actions.ViewExtra),
        create: authorizer.canExecute(actions.CreateExtra),
        modify: authorizer.canExecute(actions.ModifyExtra),
        delete: authorizer.canExecute(actions.DeleteExtra),
      },
      {
        section: SECTION.PROMOTIONS,
        show: authorizer.canExecute(actions.ViewPromo),
        create: authorizer.canExecute(actions.CreatePromo),
        modify: authorizer.canExecute(actions.ModifyPromo),
        delete: false,
      },
      {
        section: SECTION.ACCOMMODATIONS,
        show: authorizer.canExecute(actions.ViewAccomodation),
        create: authorizer.canExecute(actions.CreateAccomodation),
        modify: authorizer.canExecute(actions.ModifyAccomodation),
        delete: authorizer.canExecute(actions.DeleteAccomodation),
      },
      {
        section: SECTION.OCCUPATIONS,
        show: authorizer.canExecute(actions.ViewOccupation),
        create: authorizer.canExecute(actions.CreateOccupation),
        modify: authorizer.canExecute(actions.ModifyOccupation),
        delete: authorizer.canExecute(actions.DeleteOccupation),
      },
      {
        section: SECTION.CONDITIONS,
        show: authorizer.canExecute(actions.ViewCondition),
        create: authorizer.canExecute(actions.CreateCondition),
        modify: authorizer.canExecute(actions.ModifyCondition),
        delete: authorizer.canExecute(actions.DeleteCondition),
      },
      {
        section: SECTION.MEAL_PLANS,
        show: authorizer.canExecute(actions.ViewMealPlan),
        create: authorizer.canExecute(actions.CreateMealPlan),
        modify: authorizer.canExecute(actions.ModifyMealPlan),
        delete: authorizer.canExecute(actions.DeleteMealPlan),
      },
      {
        section: SECTION.INVENTORY_LINES,
        show: authorizer.canExecute(actions.ViewInventoryLine),
        create: authorizer.canExecute(actions.CreateInventoryLine),
        modify: authorizer.canExecute(actions.ModifyInventoryLine),
        delete: authorizer.canExecute(actions.DeleteInventoryLine),
      },
      {
        section: SECTION.INVENTORY_LINES_EXCEL,
        show: authorizer.canExecute(actions.ViewInventoryLine),
        create: authorizer.canExecute(actions.CreateInventoryLine),
        modify: authorizer.canExecute(actions.ModifyInventoryLine),
        delete: authorizer.canExecute(actions.DeleteInventoryLine),
      },
      {
        section: SECTION.DYNAMIC_PRICE_RULES,
        show: authorizer.canExecute(actions.ViewDynamicPriceRule),
        create: authorizer.canExecute(actions.CreateDynamicPriceRule),
        modify: authorizer.canExecute(actions.ModifyDynamicPriceRule),
        delete: authorizer.canExecute(actions.DeleteDynamicPriceRule),
      },
      {
        section: SECTION.GOOGLE_ANALYTICS,
        show: authorizer.canExecute(actions.ViewGoogleAnalyticsSettings),
        modify: authorizer.canExecute(actions.ModifyGoogleAnalyticsSettings),
      },
      {
        section: SECTION.CUSTOM_MARKUPS,
        show: authorizer.canExecute(actions.ViewCustomMarkup),
        create: authorizer.canExecute(actions.CreateCustomMarkup),
        modify: authorizer.canExecute(actions.ModifyCustomMarkup),
        delete: authorizer.canExecute(actions.DeleteCustomMarkup),
      },
      {
        section: SECTION.RESERVATION_STATS,
        show: authorizer.canExecute(actions.ViewReservationStats),
      },
      {
        section: SECTION.STAYS_PER_DAY,
        show: authorizer.canExecute(actions.ViewStaysPerDay),
      },
      {
        section: SECTION.EXTENDED_RESERVATION_STATS,
        show: authorizer.canExecute(actions.ViewExtendedReservationStats),
        modify: authorizer.canExecute(actions.ModifyReservation),
        delete: authorizer.canExecute(actions.CancelReservation),
      },
      {
        section: SECTION.QUERY_STATS,
        show: authorizer.canExecute(actions.ViewQueryStats),
      },
      {
        section: SECTION.BILLING_REPORT,
        show: authorizer.canExecute(actions.ViewBillingReport),
      },
      {
        section: SECTION.WAITING_LIST_REPORT,
        show: authorizer.canExecute(actions.ViewWaitingListReport),
      },
      {
        section: SECTION.WEB_MESSAGES,
        show: authorizer.canExecute(actions.ViewWebMessage),
        create: authorizer.canExecute(actions.CreateWebMessage),
        modify: authorizer.canExecute(actions.ModifyWebMessage),
        delete: authorizer.canExecute(actions.DeleteWebMessage),
      },
      {
        section: SECTION.ALERT_SETTINGS,
        show: authorizer.canExecute(actions.ViewAlertSettings),
        modify: authorizer.canExecute(actions.ModifyAlertSettings),
      },
      {
        section: SECTION.CONFIRMATION_EMAIL_SETTINGS,
        show: authorizer.canExecute(actions.ViewConfirmationEmailSettings),
        modify: authorizer.canExecute(actions.ModifyConfirmationEmailSettings),
      },
      {
        section: SECTION.EMAIL_LISTING,
        show: authorizer.canExecute(actions.ViewEmailListing),
      },
      {
        section: SECTION.PRE_STAY_EMAIL_SETTINGS,
        show: authorizer.canExecute(actions.ViewPreStayEmailSettings),
        modify: authorizer.canExecute(actions.ModifyPreStayEmailSettings),
      },
      {
        section: SECTION.POST_STAY_EMAIL_SETTINGS,
        show: authorizer.canExecute(actions.ViewPostStayEmailSettings),
        modify: authorizer.canExecute(actions.ModifyPostStayEmailSettings),
      },
      {
        section: SECTION.POST_STAY_POLL_SETTINGS,
        show: authorizer.canExecute(actions.ViewPostStayPollSettings),
        modify: authorizer.canExecute(actions.ModifyPostStayPollSettings),
      },
      {
        section: SECTION.POST_STAY_POLL_RESULTS,
        show: authorizer.canExecute(actions.ViewPostStayPollResults),
      },
      {
        section: SECTION.QUESTIONNAIRES,
        show: authorizer.canExecute(actions.ViewForm),
        create: authorizer.canExecute(actions.CreateForm),
        modify: authorizer.canExecute(actions.ModifyForm),
        delete: authorizer.canExecute(actions.DeleteForm),
      },
      {
        section: SECTION.CUSTOM_EMAIL,
        show: authorizer.canExecute(actions.ViewCustomEmail),
        create: authorizer.canExecute(actions.CreateCustomEmail),
        modify: authorizer.canExecute(actions.ModifyCustomEmail),
        delete: authorizer.canExecute(actions.DeleteCustomEmail),
      },
      {
        section: SECTION.PROPERTY_INFO,
        show: authorizer.canExecute(actions.ViewPropertyInfo),
        modify: authorizer.canExecute(actions.ModifyPropertyInfo),
      },
      {
        section: SECTION.BEHAVIOR_SETTINGS,
        show: authorizer.canExecute(actions.ViewBehaviorSettings),
        modify: authorizer.canExecute(actions.ModifyBehaviorSettings),
      },
      {
        section: SECTION.LANGUAGE_SETTINGS,
        show: authorizer.canExecute(actions.ViewLanguageSettings),
        modify: authorizer.canExecute(actions.ModifyLanguageSettings),
      },
      {
        section: SECTION.CUSTOMER_INFO_SETTINGS,
        show: authorizer.canExecute(actions.ViewCustomerInfoSettings),
        modify: authorizer.canExecute(actions.ModifyCustomerInfoSettings),
      },
      {
        section: SECTION.CURRENCY_SETTINGS,
        show: authorizer.canExecute(actions.ViewCurrencySettings),
        modify: authorizer.canExecute(actions.ModifyCurrencySettings),
      },
      {
        section: SECTION.TAX_SETTINGS,
        show: authorizer.canExecute(actions.ViewTaxSettings),
        modify: authorizer.canExecute(actions.ModifyTaxSettings),
      },
      {
        section: SECTION.CREDIT_CARD_SETTINGS,
        show: authorizer.canExecute(actions.ViewCreditCardSettings),
        modify: authorizer.canExecute(actions.ModifyCreditCardSettings),
      },
      {
        section: SECTION.PAYMENT_METHODS,
        show: authorizer.canExecute(actions.ViewPaymentMethod),
        create: false,
        modify: authorizer.canExecute(actions.ModifyPaymentMethod),
        delete: false,
      },
      {
        section: SECTION.OCCUPANT_SETTINGS,
        show: authorizer.canExecute(actions.ViewOccupantSettings),
        modify: authorizer.canExecute(actions.ModifyOccupantSettings),
      },
      {
        section: SECTION.INVENTORY_SETTINGS,
        show: authorizer.canExecute(actions.ViewInventorySettings),
        modify: authorizer.canExecute(actions.ModifyInventorySettings),
      },
      {
        section: SECTION.APPEARANCE_SETTINGS,
        show: authorizer.canExecute(actions.ViewAppeareanceSettings),
        modify: authorizer.canExecute(actions.ModifyAppeareanceSettings),
      },
      {
        section: SECTION.DISPLAY,
        show: authorizer.canExecute(actions.ViewDisplay),
        create: authorizer.canExecute(actions.CreateDisplay),
        modify: authorizer.canExecute(actions.ModifyDisplay),
        delete: authorizer.canExecute(actions.DeleteDisplay),
      },
      {
        // Blocked temporarily
        section: SECTION.CHANGE_OWN_PASSWORD,
        // show: authorizer.canExecute(actions.ChangeOwnPassword),
        show: false,
      },
      {
        section: SECTION.LEGAL_INFO,
        show: authorizer.canExecute(actions.ViewLegalInfo),
        create: authorizer.canExecute(actions.CreateLegalInfo),
        modify: authorizer.canExecute(actions.ModifyLegalInfo),
        delete: authorizer.canExecute(actions.DeleteLegalInfo),
      },
      {
        section: SECTION.RESERVATION_MODIFICATION_POLICY,
        show: authorizer.canExecute(actions.ViewReservationModificationPolicy),
        create: authorizer.canExecute(actions.CreateReservationModificationPolicy),
        modify: authorizer.canExecute(actions.ModifyReservationModificationPolicy),
        delete: authorizer.canExecute(actions.DeleteReservationModificationPolicy),
      },
      {
        section: SECTION.FILTERS,
        show: authorizer.canExecute(actions.ViewFilter),
        create: authorizer.canExecute(actions.CreateFilter),
        modify: authorizer.canExecute(actions.ModifyFilter),
        delete: authorizer.canExecute(actions.DeleteFilter),
        import: authorizer.canExecute(actions.CreateFilter),
        export: authorizer.canExecute(actions.CreateFilter),
      },
      {
        section: SECTION.CANCELLATION_POLICY,
        show: authorizer.canExecute(actions.ViewCancellationPolicy),
        create: authorizer.canExecute(actions.CreateCancellationPolicy),
        modify: authorizer.canExecute(actions.ModifyCancellationPolicy),
        delete: authorizer.canExecute(actions.DeleteCancellationPolicy),
      },
      {
        section: SECTION.TAGS_MANAGER,
        show: authorizer.canExecute(actions.ViewTags),
        modify: authorizer.canExecute(actions.ModifyTags),
        import: authorizer.canExecute(actions.CreateTags),
        export: authorizer.canExecute(actions.CreateTags),
      },
      {
        section: SECTION.MAPS_MANAGER,
        show: authorizer.canExecute(actions.ViewCustomMaps),
        modify: authorizer.canExecute(actions.ModifyCustomMaps),
      },
      {
        section: SECTION.PROPERTY_USERS,
        show: authorizer.canExecute(actions.ViewPropertyUsers),
      },
      {
        section: SECTION.USER_ACCESS_TO_PROPERTY,
        show: authorizer.canExecute(actions.ModifyAccessToProperty),
      },
      {
        section: SECTION.CREATE_PROPERTY,
        show: authorizer.canExecute(actions.CreateProperty),
      },
      {
        section: SECTION.CREATE_USER,
        show: authorizer.canExecute(actions.CreateUser),
      },
      {
        section: SECTION.CHANGE_USER_PASSWORD,
        show: authorizer.canExecute(actions.ChangeUserPassword),
      },
      {
        section: SECTION.REPLICATE_PROPERTY,
        show: authorizer.canExecute(actions.ReplicateProperty),
      },
      {
        section: SECTION.EXECUTE_BATCH_SQL_QUERY,
        show: authorizer.canExecute(actions.ExecuteBatchSQLQuery),
      },
      {
        section: SECTION.GLOBAL_EMAIL_LIST,
        show: authorizer.canExecute(actions.ViewGlobalEmailList),
      },
      {
        section: SECTION.SITEMINDER_SETTINGS,
        show: authorizer.canExecute(actions.ViewSiteminderSettings),
        modify: authorizer.canExecute(actions.ModifySiteminderSettings),
      },
      {
        section: SECTION.TRIPADVISOR_RE_SETTINGS,
        show: authorizer.canExecute(actions.ViewTripAdvisorRESettings),
        modify: authorizer.canExecute(actions.ModifyTripAdvisorRESettings),
      },
      {
        section: SECTION.RATEGAIN_SETTINGS,
        show: authorizer.canExecute(actions.ViewRategainSettings),
        modify: authorizer.canExecute(actions.ModifyRategainSettings),
      },
      {
        section: SECTION.PARITYRATE_SETTINGS,
        show: authorizer.canExecute(actions.ViewParityRateSettings),
        modify: authorizer.canExecute(actions.ModifyParityRateSettings),
      },
      {
        section: SECTION.INTEGRATION_PARAMETERS,
        show: authorizer.canExecute(actions.ViewIntegrationParameters),
        modify: authorizer.canExecute(actions.ModifyIntegrationParameters),
      },
      {
        section: SECTION.PROPERTY_CONFIGURATION,
        show: authorizer.canExecute(actions.ViewPropertyConfiguration),
        modify: authorizer.canExecute(actions.ModifyPropertyConfiguration),
      },
      {
        section: SECTION.CHANNEL_LOGS,
        show: authorizer.canExecute(actions.ViewChannelLogs),
      },
      {
        section: SECTION.RESEND_RESERVATION_TO_CHANNEL,
        show: authorizer.canExecute(actions.ResendReservationToChannel),
      },
      {
        section: SECTION.CRM_CLIENT,
        show: authorizer.canExecute(actions.ViewClient),
        create: authorizer.canExecute(actions.CreateClient),
        modify: authorizer.canExecute(actions.ModifyClient),
        delete: authorizer.canExecute(actions.DeleteClient),
        export: authorizer.canExecute(actions.ViewClient),
      },
      {
        section: SECTION.CRM_PROPERTY,
        show: authorizer.canExecute(actions.ViewCrmProperty),
        create: false,
        modify: authorizer.canExecute(actions.ModifyCrmProperty),
        delete: false,
        export: authorizer.canExecute(actions.ViewCrmProperty),
      },
      {
        section: SECTION.CRM_SERVICE,
        show: authorizer.canExecute(actions.ViewService),
        create: false,
        modify: false,
        delete: false,
        export: authorizer.canExecute(actions.ViewService),
      },
      {
        section: SECTION.CRM_CONTRACT,
        show: authorizer.canExecute(actions.ViewContract),
        create: authorizer.canExecute(actions.CreateContract),
        modify: authorizer.canExecute(actions.ModifyContract),
        delete: false,
        export: authorizer.canExecute(actions.ViewContract),
      },
      {
        section: SECTION.CRM_PARTNER,
        show: authorizer.canExecute(actions.ViewPartner),
        create: authorizer.canExecute(actions.CreatePartner),
        modify: authorizer.canExecute(actions.ModifyPartner),
        delete: false,
        export: authorizer.canExecute(actions.ViewPartner),
      },
      {
        section: SECTION.CRM_BILLING_REPORT,
        show: authorizer.canExecute(actions.ViewCRMBillingReport),
      },
      {
        section: SECTION.PROPERTY_PARAMETERS,
        show: authorizer.canExecute(actions.ViewPropertyParameters),
        modify: authorizer.canExecute(actions.ModifyPropertyParameters),
      },
      {
        section: SECTION.FEES_MANAGER,
        show: authorizer.canExecute(actions.ViewTaxes),
        create: authorizer.canExecute(actions.CreateTaxes),
        modify: authorizer.canExecute(actions.ModifyTaxes),
        delete: authorizer.canExecute(actions.DeleteTaxes),
      },
      {
        section: SECTION.TAXES_MANAGER,
        show: authorizer.canExecute(actions.ViewTaxes),
        create: authorizer.canExecute(actions.CreateTaxes),
        modify: authorizer.canExecute(actions.ModifyTaxes),
        delete: authorizer.canExecute(actions.DeleteTaxes),
      },
      {
        section: SECTION.CHECK_INVENTORY_INTEGRITY,
        show: authorizer.canExecute(actions.CheckInventoryIntegrity),
      },
      {
        section: SECTION.PROPERTY_CHANNEL_MANAGERS,
        show: authorizer.canExecute(actions.UpdatePropertyChannelManagers),
      },
      {
        section: SECTION.CREATE_USER_BATCH,
        show: authorizer.canExecute(actions.CreateUserBatch),
      },
      {
        section: SECTION.EDIT_USER_BATCH,
        show: authorizer.canExecute(actions.EditUserBatch),
      },
      {
        section: SECTION.GENERATE_BILLING_REPORT,
        show: authorizer.canExecute(actions.GenerateBillingReport),
      },
      {
        section: SECTION.CHAIN_CONFIGURER,
        show: authorizer.canExecute(actions.ViewChainConfigurer),
      },
      {
        section: SECTION.QUERY_EXECUTOR,
        show: authorizer.canExecute(actions.ViewQueryExecutor),
      },
      {
        section: SECTION.GOOGLE_FEED,
        show: authorizer.canExecute(actions.ViewGoogleFeed),
      },
      {
        section: SECTION.ARI_SNAPSHOT,
        show: authorizer.canExecute(actions.ViewAriSnapshot),
      },
      // TODO: create action
      {
        section: SECTION.PACKAGES,
        show: true,
        create: true,
        modify: true,
        delete: true,
      },
      //TODO: Refactor for packages
      {
        section: SECTION.BUNDLES,
        show: authorizer.canExecute(actions.ViewBundles),
      },
      {
        section: SECTION.BUNDLES_CREATE,
        show: authorizer.canExecute(actions.ViewBundles),
        modify: authorizer.canExecute(actions.ModifyBundles),
        create: authorizer.canExecute(actions.CreateBundles),
      },
      {
        section: SECTION.BUNDLES_EDIT,
        show: authorizer.canExecute(actions.ViewBundles),
        modify: authorizer.canExecute(actions.ModifyBundles),
        create: authorizer.canExecute(actions.CreateBundles),
      },
      {
        section: SECTION.QUOTATION,
        show: authorizer.canExecute(actions.ViewQuotation),
        modify: authorizer.canExecute(actions.ModifyQuotation),
        create: authorizer.canExecute(actions.CreateQuotation),
        delete: authorizer.canExecute(actions.DeleteQuotation),
      },
      // TODO: create action
      {
        section: SECTION.PRICE_FEED,
        show: true,
      },
      // TODO: create action
      {
        section: SECTION.CHANGE_USER_GROUP,
        show: authorizer.canExecute(actions.CreateProperty),
      },
      // TODO: create action
      {
        section: SECTION.CONNECTIONS_WIT_CHANNEL,
        show: authorizer.canExecute(actions.CreateProperty),
      },
      // TODO: create action
      {
        section: SECTION.USERS_JAVA_PROPERTY_TO_EXCEL,
        show: authorizer.canExecute(actions.CreateProperty),
      },
      // TODO: create action
      {
        section: SECTION.GOOGLE_HOTEL_FINDER,
        show: authorizer.canExecute(actions.CreateProperty),
      },
      // TODO: create action
      {
        section: SECTION.COMPARE_DATABASE,
        show: authorizer.canExecute(actions.ViewPropertyParameters),
      },
      // TODO: create action
      {
        section: SECTION.SYSTEM_CONTROL_PANEL,
        show: false,
      },
      // TODO: create action
      {
        section: SECTION.BILLING_EVOLUTION,
        show: false,
      },
      // TODO: create action
      {
        section: SECTION.INVENTORY_CONFIGURATION,
        show: isViewAuthorized(authorizer, [
          actions.ViewAccomodation,
          actions.ViewOccupation,
          actions.ViewCondition,
          actions.ViewMealPlan,
          actions.ViewInventoryLine,
        ]),
      },
      {
        section: SECTION.LOYALTY_CUSTOMERS,
        show: authorizer.canExecute(actions.ViewLoyaltyCustomers),
        create: authorizer.canExecute(actions.CreateLoyaltyCustomers),
        modify: authorizer.canExecute(actions.ModifyLoyaltyCustomers),
        delete: authorizer.canExecute(actions.DeleteLoyaltyCustomers),
      },
      {
        section: SECTION.LOYALTY_CUSTOMER_TRANSACTION_REPORT,
        show: authorizer.canExecute(actions.ViewLoyaltyCustomers),
        create: authorizer.canExecute(actions.CreateLoyaltyCustomers),
        modify: authorizer.canExecute(actions.ModifyLoyaltyCustomers),
        delete: authorizer.canExecute(actions.DeleteLoyaltyCustomers),
      },
      {
        section: SECTION.LOYALTY_DERIVED_TIERS,
        show: authorizer.canExecute(actions.ViewDerivedLoyaltyTiers),
        create: authorizer.canExecute(actions.CreateDerivedLoyaltyTiers),
        modify: authorizer.canExecute(actions.ModifyDerivedLoyaltyTiers),
        delete: authorizer.canExecute(actions.DeleteDerivedLoyaltyTiers),
      },
      {
        section: SECTION.LOYALTY_CONSTANT_TIERS,
        show: authorizer.canExecute(actions.ViewConstantLoyaltyTiers),
        create: authorizer.canExecute(actions.CreateConstantLoyaltyTiers),
        modify: authorizer.canExecute(actions.ModifyConstantLoyaltyTiers),
        delete: authorizer.canExecute(actions.DeleteConstantLoyaltyTiers),
      },
      {
        section: SECTION.LOYALTY_ORGANIZATIONS,
        show: authorizer.canExecute(actions.ViewLoyaltyOrganizations),
        create: authorizer.canExecute(actions.CreateLoyaltyOrganizations),
        modify: authorizer.canExecute(actions.ModifyLoyaltyOrganizations),
        delete: authorizer.canExecute(actions.DeleteLoyaltyOrganizations),
      },
      {
        section: SECTION.LOYALTY_HISTORICAL,
        show: authorizer.canExecute(actions.ViewLoyaltyCustomerHistorical),
        modify: authorizer.canExecute(actions.ModifyLoyaltyCustomerHistorical),
      },
      {
        section: SECTION.TAX_CONFIGURATION,
        show: authorizer.canExecute(actions.ViewTaxConfiguration),
        create: authorizer.canExecute(actions.ModifyTaxConfiguration),
        modify: authorizer.canExecute(actions.ModifyTaxConfiguration),
        delete: authorizer.canExecute(actions.ModifyTaxConfiguration),
      },
      {
        section: SECTION.LOYALTY_LINKS_GENERATOR,
        show: authorizer.canExecute(actions.ViewLoyaltyCustomers),
      },
      {
        section: SECTION.PROPERTY_CALENDAR_REGEN,
        show: authorizer.canExecute(actions.ViewCalendarRegen),
      },
      {
        section: SECTION.MIGRATE_RESERVATION,
        show: authorizer.canExecute(actions.ViewAriSnapshot),
      },
      {
        section: SECTION.GTM_MANAGER,
        show: authorizer.canExecute(actions.ViewAriSnapshot),
      },
      {
        section: SECTION.UPGRADE_TIER,
        // TODO: uncomment when back-end is implemented
        // show: authorizer.canExecute(actions.ViewLoyaltyCustomerHistorical),
        // modify: authorizer.canExecute(actions.ModifyLoyaltyCustomerHistorical),
        show: false,
        modify: false,
      },
      {
        section: SECTION.EMAIL_BLACKLIST,
        show: authorizer.canExecute(actions.ShowEmailBlacklist),
      },
      {
        section: SECTION.ENGINE_MULTI_CONFIG,
        show: authorizer.canExecute(actions.ViewEngineMultiConfig),
        create: authorizer.canExecute(actions.CreateEngineMultiConfig),
        modify: authorizer.canExecute(actions.ModifyEngineMultiConfig),
        delete: authorizer.canExecute(actions.DeleteEngineMultiConfig),
      },
    ];
  }, [authorizer]);
}
